import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Boton from '../../components/Boton'
import CampoFecha from '../../components/CampoFecha'
import MoneyInput from '../../components/MoneyInput'
import BuscadorProveedor from '../../components/BuscadorProveedor'
import { npApi } from '../../api/compras'
import { colors, text, space } from '../../design/tokens'
import { nuevoUuid, mensajeError, st } from '../emp900/_helpers'
import { SelectDeposito, CajaError, Campo } from '../emp900/_comun'
import RenglonesNpEditor from './RenglonesNpEditor'
import ModalListaPreciosProveedor from './ModalListaPreciosProveedor'
import { CONDICION_TIPO, MONEDAS, renglonValido, renglonVacio, renglonDesdeItem } from './_np'
import useDirtyGuard from '../../hooks/useDirtyGuard'

const condicionNueva = () => ({ tipo: 'general', descripcion: '', porcentaje: '', importe: '', lleva: '', paga: '', vigencia_hasta: '', origen: 'manual' })
const CAB = { fecha: '', condicion_pago_id: '', moneda: 'ARS', cotizacion: '', bonificacion_pct: '', lugar_entrega: '', fecha_entrega: '', numero_pedido_proveedor: '', deposito: '', observaciones: '' }

/**
 * Ingreso / Modificación de una Nota de Pedido (solo en borrador). Cabecera y
 * renglones con los campos que fijó Martin (1 sep 2026). Se guardan los
 * COMPONENTES del precio; el backend recalcula. Emitir, enviar y
 * seguir se hace desde el detalle.
 */
export default function NotaPedidoFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const esEdicion = !!id
  const [opUuid] = useState(nuevoUuid)      // una clave por guardado: reintentar = misma clave
  const [proveedor, setProveedor] = useState(null)
  const [f, setF] = useState(CAB)
  const [renglones, setRenglones] = useState([])
  const [condiciones, setCondiciones] = useState([])
  const [verCondiciones, setVerCondiciones] = useState(false)
  const [cat, setCat] = useState({ condiciones_pago: [], monedas: MONEDAS, depositos: [] })
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [cargando, setCargando] = useState(esEdicion)
  const [sucio, setSucio] = useState(false)
  const [verLista, setVerLista] = useState(false)
  const [listaVersion, setListaVersion] = useState(0) // sube al activar una lista: la grilla vuelve a pedir precios
  useDirtyGuard(sucio)

  useEffect(() => { npApi.catalogos().then(({ data }) => setCat(c => ({ ...c, ...data, monedas: data.monedas?.length ? data.monedas : MONEDAS }))).catch(() => { /* sin catálogos: los selectores quedan vacíos y el guardado avisa */ }) }, [])

  useEffect(() => {
    if (!esEdicion) return
    npApi.obtener(id).then(async ({ data }) => {
      const p = data.pedido
      if (p.estado !== 'borrador') { navigate(`/compras/pedidos/${id}`, { replace: true }); return }
      setF({
        fecha: p.fecha || '', condicion_pago_id: p.condicion_pago_id ? String(p.condicion_pago_id) : '', moneda: p.moneda || 'ARS',
        cotizacion: p.cotizacion ? String(Number(p.cotizacion)) : '', bonificacion_pct: Number(p.bonificacion_pct) ? String(Number(p.bonificacion_pct)) : '',
        lugar_entrega: p.lugar_entrega || '', fecha_entrega: p.fecha_entrega || '', numero_pedido_proveedor: p.numero_pedido_proveedor || '',
        deposito: p.deposito || '', observaciones: p.observaciones || '',
      })
      setRenglones((p.items || []).map(renglonDesdeItem))
      const conds = (p.condiciones || []).map(c => ({ ...condicionNueva(), ...c, porcentaje: c.porcentaje ?? '', importe: c.importe ?? '', lleva: c.lleva ?? '', paga: c.paga ?? '', vigencia_hasta: c.vigencia_hasta || '' }))
      setCondiciones(conds); setVerCondiciones(conds.length > 0)
      if (p.proveedor_lgr) setProveedor(p.proveedor_lgr)
    }).catch(e => setError(mensajeError(e))).finally(() => setCargando(false))
  }, [esEdicion, id, navigate])

  const set = (k) => (v) => { setSucio(true); setF(s => ({ ...s, [k]: v?.target ? v.target.value : v })) }
  const setR = (r) => { setSucio(true); setRenglones(r) }
  const setC = (i, k, v) => { setSucio(true); setCondiciones(cs => cs.map((c, j) => j === i ? { ...c, [k]: v } : c)) }
  const conProductos = renglones.filter(r => !renglonVacio(r))
  const esUsd = f.moneda !== 'ARS'
  const listo = proveedor && conProductos.length > 0 && conProductos.every(renglonValido) && (!esUsd || Number(f.cotizacion) > 0) && condiciones.every(c => c.descripcion.trim())

  const guardar = async () => {
    setError(''); setGuardando(true)
    try {
      const payload = {
        op_uuid: opUuid, proveedor_id: proveedor.id,
        fecha: f.fecha || undefined, fecha_entrega: f.fecha_entrega || undefined, deposito: f.deposito || undefined,
        condicion_pago_id: f.condicion_pago_id || undefined, moneda: f.moneda, cotizacion: esUsd ? f.cotizacion : undefined,
        bonificacion_pct: f.bonificacion_pct || 0, lugar_entrega: f.lugar_entrega || undefined, numero_pedido_proveedor: f.numero_pedido_proveedor || undefined,
        observaciones: f.observaciones || undefined,
        renglones: conProductos.map(r => ({
          sku: r.sku, cantidad: r.cantidad, precio_lista: r.precio_lista,
          descuento1_pct: r.descuento1_pct || 0, descuento2_pct: r.descuento2_pct || 0, descuento3_pct: r.descuento3_pct || 0,
          iva_pct: r.iva_pct === '' ? 21 : r.iva_pct, internos_pct: r.internos_pct === '' ? undefined : r.internos_pct,
          codigo_proveedor: r.codigo_proveedor || undefined, ean: r.ean || undefined, origen_precio: r.origen_precio || 'manual',
        })),
        condiciones: condiciones.map(c => ({
          tipo: c.tipo, descripcion: c.descripcion, porcentaje: c.porcentaje || undefined, importe: c.importe || undefined,
          lleva: c.lleva || undefined, paga: c.paga || undefined, sku: c.sku || undefined, vigencia_hasta: c.vigencia_hasta || undefined, origen: c.origen || 'manual',
        })),
      }
      const { data } = esEdicion ? await npApi.editar(id, payload) : await npApi.crear(payload)
      setSucio(false)
      navigate(`/compras/pedidos/${data.pedido?.id || id}`, { replace: true })
    } catch (e) { setError(mensajeError(e)) } finally { setGuardando(false) }
  }

  if (cargando) return <p style={st.info}>Cargando…</p>

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>{esEdicion ? 'Modificación de pedido' : 'Ingreso de pedido'}</h2>
        <div style={st.acciones}>
          <Boton variante="ghost" onClick={() => navigate('/compras/pedidos/consultas')}>← Consultas</Boton>
          <Boton variante="primary" disabled={!listo || guardando} onClick={guardar}>{guardando ? 'Guardando…' : (esEdicion ? 'Guardar cambios' : 'Guardar pedido')}</Boton>
        </div>
      </div>
      <p style={st.sub}>Queda en <b>borrador</b>: se puede modificar todo. Al <b>emitir</b> (desde el detalle) se congela lo acordado y se puede enviar al proveedor.</p>
      {error && <CajaError>{error}</CajaError>}

      <div style={st.card}>
        <div style={st.cardTitulo}>Pedido</div>
        <div style={st.seccion}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: space.md }}>
            <Campo label="Proveedor (🔍 o F6 para buscar · + para dar de alta)"><BuscadorProveedor value={proveedor} onChange={p => { setSucio(true); setProveedor(p) }} />
              {proveedor && <div style={{ marginTop: space.xs }}><Boton variante="secondary" sm onClick={() => setVerLista(true)}>📄 Lista de precios</Boton></div>}</Campo>
            <Campo label="Fecha (vacío = hoy)"><CampoFecha style={st.input} value={f.fecha} onChange={set('fecha')} /></Campo>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: space.md }}>
            <Campo label="Condición">
              <select style={st.input} value={f.condicion_pago_id} onChange={set('condicion_pago_id')}>
                <option value="">— elegir —</option>
                {(cat.condiciones_pago || []).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Moneda">
              <select style={st.input} value={f.moneda} onChange={e => { setSucio(true); setF(s => ({ ...s, moneda: e.target.value, cotizacion: e.target.value === 'ARS' ? '' : s.cotizacion })) }}>
                {(cat.monedas || MONEDAS).map(m => <option key={m.codigo} value={m.codigo}>{m.nombre} ({m.codigo})</option>)}
              </select>
            </Campo>
            <Campo label={esUsd ? 'Cotización (obligatoria)' : 'Cotización'}>
              <MoneyInput style={{ ...st.input, textAlign: 'right' }} value={f.cotizacion} onChange={set('cotizacion')} disabled={!esUsd} placeholder={esUsd ? '$ por 1 USD' : 'en pesos no aplica'} />
            </Campo>
            <Campo label="Bonificación general %">
              <input style={{ ...st.input, textAlign: 'right' }} inputMode="decimal" value={f.bonificacion_pct} onChange={e => set('bonificacion_pct')(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))} />
            </Campo>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: space.md }}>
            <Campo label="Lugar de entrega"><input style={st.input} value={f.lugar_entrega} onChange={set('lugar_entrega')} placeholder="Dirección o referencia donde entrega el proveedor" /></Campo>
            <Campo label="Fecha de entrega"><CampoFecha style={st.input} value={f.fecha_entrega} onChange={set('fecha_entrega')} /></Campo>
            <Campo label="Nº pedido del proveedor"><input style={st.input} value={f.numero_pedido_proveedor} onChange={set('numero_pedido_proveedor')} /></Campo>
            <Campo label="Depósito"><SelectDeposito value={f.deposito} onChange={set('deposito')} depositos={cat.depositos || []} /></Campo>
          </div>
        </div>
      </div>

      <div style={{ ...st.card, marginTop: space.lg }}>
        <div style={st.cardTitulo}>Ingreso de productos</div>
        <RenglonesNpEditor renglones={renglones} onChange={setR} bonifPct={f.bonificacion_pct} moneda={f.moneda} proveedorId={proveedor?.id || null} listaVersion={listaVersion} />
      </div>

      <div style={{ ...st.card, marginTop: space.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={st.cardTitulo}>Observaciones (van en el reporte)</div>
          {!verCondiciones && <Boton variante="ghost" sm onClick={() => setVerCondiciones(true)}>+ Condiciones comerciales especiales (opcional)</Boton>}
        </div>
        <textarea style={st.textarea} rows={3} value={f.observaciones} onChange={set('observaciones')} />
      </div>

      {verCondiciones && (
        <div style={{ ...st.card, marginTop: space.lg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={st.cardTitulo}>Condiciones comerciales especiales</div>
            <Boton variante="secondary" sm onClick={() => { setSucio(true); setCondiciones(cs => [...cs, condicionNueva()]) }}>+ Condición</Boton>
          </div>
          <p style={{ ...st.info, margin: `0 0 ${space.sm}px` }}>Acuerdos particulares que no entran en los descuentos del renglón (bonificaciones 10+2, precios especiales, vigencias). Quedan congelados con el pedido al emitir.</p>
          {condiciones.length === 0 ? <p style={st.info}>Sin condiciones especiales.</p> : (
            <div style={st.tableWrap}><table style={st.table}>
              <thead><tr>{['Tipo', 'Descripción', '%', '$', 'Lleva', 'Paga', 'Vigencia hasta', ''].map(h => <th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {condiciones.map((c, i) => (
                  <tr key={i} style={st.tr}>
                    <td style={st.td}><select style={st.inputCelda} value={c.tipo} onChange={e => setC(i, 'tipo', e.target.value)}>{Object.entries(CONDICION_TIPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                    <td style={st.td}><input style={st.inputCelda} value={c.descripcion} placeholder="Ej.: 5% por pago a 7 días" onChange={e => setC(i, 'descripcion', e.target.value)} /></td>
                    <td style={st.td}><input style={{ ...st.inputCelda, width: 64, textAlign: 'right' }} inputMode="decimal" value={c.porcentaje} onChange={e => setC(i, 'porcentaje', e.target.value.replace(/[^\d.]/g, ''))} /></td>
                    <td style={st.td}><MoneyInput style={{ ...st.inputCelda, width: 96, textAlign: 'right' }} value={c.importe} onChange={v => setC(i, 'importe', v)} /></td>
                    <td style={st.td}><input style={{ ...st.inputCelda, width: 56, textAlign: 'right' }} inputMode="numeric" value={c.lleva} onChange={e => setC(i, 'lleva', e.target.value.replace(/\D/g, ''))} /></td>
                    <td style={st.td}><input style={{ ...st.inputCelda, width: 56, textAlign: 'right' }} inputMode="numeric" value={c.paga} onChange={e => setC(i, 'paga', e.target.value.replace(/\D/g, ''))} /></td>
                    <td style={st.td}><CampoFecha style={{ ...st.inputCelda, width: 120 }} value={c.vigencia_hasta} onChange={v => setC(i, 'vigencia_hasta', v?.target ? v.target.value : v)} /></td>
                    <td style={st.td}><button type="button" style={st.quitar} onClick={() => { setSucio(true); setCondiciones(cs => cs.filter((_, j) => j !== i)) }} aria-label="Quitar">×</button></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      <div style={st.pie}>
        <Boton variante="ghost" onClick={() => navigate('/compras/pedidos/consultas')}>Volver sin guardar</Boton>
        <Boton variante="primary" disabled={!listo || guardando} onClick={guardar}>{guardando ? 'Guardando…' : (esEdicion ? 'Guardar cambios' : 'Guardar pedido')}</Boton>
      </div>
      {verLista && proveedor && <ModalListaPreciosProveedor proveedor={proveedor} onClose={() => setVerLista(false)} onActivada={() => setListaVersion(v => v + 1)} />}
      {!listo && conProductos.length > 0 && <p style={{ ...st.info, textAlign: 'right', color: colors.textMeta, fontSize: text.sec }}>{esUsd && !(Number(f.cotizacion) > 0) ? 'Falta la cotización del dólar. ' : ''}Cada renglón necesita un código válido, cantidad y precio de lista.</p>}
    </div>
  )
}
